import { isEqual } from "lodash";
import { createContext, Dispatch, useContext, useEffect, useReducer } from "react";
import { useArsContext } from "../contexts";
import { arsTableReducer } from "./arsTableReducer";
import { buildArsTableScaffoldModel, computeUsedGroupingFactorIds } from "./functions";
import {
  ARST_CHANGE_SENT,
  ARST_LOAD_DATA,
  ARST_SET_ANALYSIS_METHOD,
  ARST_SET_TLF_VIEW_MODE,
  ARST_UPDATE_SCAFFOLD,
  ArsTableAction,
  ArsTableBody,
  ArsTableRow,
  ArsTableSettings,
  ArsTableState,
} from "./interface";

/**
 * Value exposed to children inside an ARS table context.
 */
interface ArsTableContextProps<T> {
  /** Current reducer state for the rendered table. */
  state: ArsTableState<T>;
  /** Dispatches table actions through the reducer. */
  dispatch: Dispatch<ArsTableAction<T>>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ArsTableContext = createContext<ArsTableContextProps<any> | null>(null);

/**
 * Props for the ARS table context provider.
 */
interface ArsTableContextProviderProps<T> {
  /** Child elements that consume table state. */
  children: React.ReactNode;
  /** Persisted table body being edited. */
  bodyData: ArsTableBody<T>;
  /** Calculated rows corresponding to the table body. */
  calculatedTableRows: ArsTableRow<T>[] | null;
  /** Table rendering and editing settings. */
  settings: ArsTableSettings<T>;
  /** Publishes body changes and their calculated rows to the parent. */
  updateBodyData: (newBody: ArsTableBody<T>, calculatedBody: ArsTableRow<T>[]) => void;
}

/**
 * Provider that supplies listing state and dispatch to child components.
 *
 * @param props - Component props
 * @param props.children - Child nodes wrapped by the provider
 * @param props.bodyData - Body data to render
 * @param props.calculatedTableRows - Calculated table rows to render
 * @param props.settings - Table settings including view mode and other options
 * @param props.updateBodyData - Function to partially update the body data
 * @returns ArsTableContextProvider component
 */
export const ArsTableContextProvider = <T,>({
  children,
  bodyData,
  calculatedTableRows,
  settings,
  updateBodyData,
}: ArsTableContextProviderProps<T>) => {
  const { state: arsContextState } = useArsContext<T>();
  const [state, dispatch] = useReducer(arsTableReducer, {
    analysisMethod: arsContextState.analysisMethods[bodyData.analysisMethodId],
    bodyData,
    scaffoldModel: buildArsTableScaffoldModel(calculatedTableRows, settings.editorProps),
    calculatedTableRows,
    settings,
    changed: false,
    usedGroupingFactorIds: computeUsedGroupingFactorIds(bodyData),
    viewItem: undefined,
  });

  // Update the stored analysis method if it has been updated via bodyData or in the parent context
  useEffect(() => {
    const analysisMethod = arsContextState.analysisMethods[state.bodyData.analysisMethodId];
    if (
      !isEqual(state.analysisMethod, analysisMethod) ||
      state.bodyData.analysisMethodId !== (state.analysisMethod?.id ?? "")
    ) {
      dispatch({ operation: ARST_SET_ANALYSIS_METHOD, analysisMethod });
    }
  }, [arsContextState.analysisMethods, state.analysisMethod, state.bodyData.analysisMethodId]);

  // Synchronise any changes with the parent for body data and internally calculated rows
  useEffect(() => {
    const eq = isEqual(state.bodyData, bodyData);
    // Send changes up
    if (!eq && state.bodyData.id === bodyData.id && state.changed) {
      updateBodyData(
        state.bodyData,
        state.calculatedTableRows ??
          // istanbul ignore next
          [],
      );
      dispatch({ operation: ARST_CHANGE_SENT });
    }
    // Accept new input if it differs from current state and wasn't just sent
    else if (state.bodyData.id !== bodyData.id || !eq) {
      dispatch({ operation: ARST_LOAD_DATA, bodyData });
    }
  }, [bodyData, state.bodyData, state.calculatedTableRows, state.changed, updateBodyData]);

  // Synchronise any tlfViewMode changes
  useEffect(() => {
    if (!isEqual(state.settings.tlfViewMode, settings.tlfViewMode)) {
      dispatch({ operation: ARST_SET_TLF_VIEW_MODE, tlfViewMode: settings.tlfViewMode });
    }
  }, [settings.tlfViewMode, state.settings.tlfViewMode]);

  // Synchronise any calculated row changes from the parent
  useEffect(() => {
    const eq = isEqual(state.calculatedTableRows, calculatedTableRows);
    if (!eq && calculatedTableRows) {
      dispatch({ operation: ARST_UPDATE_SCAFFOLD, calculatedTableRows });
    }
  }, [calculatedTableRows, state.calculatedTableRows]);

  return (
    <ArsTableContext.Provider value={{ state, dispatch }}>{children}</ArsTableContext.Provider>
  );
};

ArsTableContextProvider.displayName = "ArsTableContextProvider";

/**
 * Custom hook to access the ArsTable context.
 *
 * @returns The table context object
 */
export const useArsTable = <T = string,>() => {
  const context = useContext(ArsTableContext);
  return !context
    ? {
        state: null,
        dispatch: () => console.error("WARNING: Dispatch not initiated"),
      }
    : (context as ArsTableContextProps<T>);
};
